
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Filter, X } from "lucide-react";
import { Filters, FilterOptions } from "@/types/bag";
import { useState } from "react";

interface BagFiltersProps {
  filters: Filters;
  filterOptions: FilterOptions;
  onFilterChange: (key: keyof Filters, value: string | null) => void;
  onClearFilters: () => void;
}

export function BagFilters({
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
}: BagFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasActiveFilters = filters.category || filters.year || filters.color;
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center gap-2">
          {!isOpen && (
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="relative w-9 h-9"
              >
                <Filter className="h-4 w-4" />
                {hasActiveFilters && (
                  <Badge 
                    variant="secondary" 
                    className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs rounded-full"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </CollapsibleTrigger>
          )}
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Clear all
            </Button>
          )}
        </div>

        <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
          <div className="relative p-4 border rounded-lg bg-white shadow-sm mt-4">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 w-6 h-6 rounded-full"
              >
                <X className="h-4 w-4" />
              </Button>
            </CollapsibleTrigger>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={filters.category || ""}
                  onValueChange={(value) => onFilterChange("category", value || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {filterOptions?.categories.filter(Boolean).map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Year</Label>
                <Select
                  value={filters.year || ""}
                  onValueChange={(value) => onFilterChange("year", value || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {filterOptions?.years.filter(Boolean).map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={filters.color || ""}
                  onValueChange={(value) => onFilterChange("color", value || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent>
                    {filterOptions?.colors.filter(Boolean).map((color) => (
                      <SelectItem key={color} value={color}>
                        {color}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {Object.entries(filters).map(([key, value]) => {
            if (!value) return null;
            return (
              <Badge
                key={key}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {key}: {value}
                <button
                  onClick={() => onFilterChange(key as keyof Filters, null)}
                  className="ml-1 hover:opacity-80"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
